import api from '@/shared/api';
import {
  AuditUserResponse,
  withQuery,
  type ApiResponse,
  type MemberSearchField,
  type SortOrder,
  type BulkDeleteResult,
  buildTruthySearchParams,
  requestExcelBlobExport,
} from '@/shared/lib';
import type { AxiosResponse } from 'axios';

interface MemberResponse<T = unknown> {
  success: boolean;
  data: T;
  metadata?: { total: number; page: number; itemsPerPage: number; totalPages: number };
}

export type MemberAuditUser = AuditUserResponse;

export interface MemberPosition {
  id: number;
  name: string;
  department?: { id: number; code: string } | null;
}

export interface MemberDetailResponse {
  id: number;
  user: {
    id: number;
    name: string;
    username: string | null;
    email: string;
    registrationNumber: string | null;
    status?: string | null;
    deletedAt?: string | null;
  };
  positions: MemberPosition[];
  functionalActivities: { id: number; name: string }[];
  creator: MemberAuditUser | null;
  updater: MemberAuditUser | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateMemberPayload {
  positionIds?: number[];
  functionalActivityIds?: number[];
}

export interface BranchMember {
  id?: number;
  user?: {
    id: number;
    name: string;
    username?: string | null;
    email?: string;
    registrationNumber?: string | null;
    status?: string | null;
    deletedAt?: string | null;
  };
  positions?: MemberPosition[];
  functionalActivities?: { id: number; name: string }[];
  createdAt?: string;
  updatedAt?: string;
  creator?: MemberAuditUser | null;
  updater?: MemberAuditUser | null;
}

export interface MembersFilter {
  positionId?: number;
  departmentId?: number;
  functionalActivityId?: number;
  status?: 'ACTIVE' | 'INACTIVE';
  includeInactiveMembers?: boolean;
}

export interface MemberPermissionQuery {
  requiredPermissionAction?: string;
  requiredPermissionSubject?: string;
  requiredPermissions?: string[];
  requiredPermissionsMatchMode?: 'any' | 'all';
  excludeCurrentUser?: boolean;
}

export interface ExportMembersExcelParams {
  search?: string;
  searchField?: MemberSearchField;
  sort?: string;
  order?: SortOrder;
  positionId?: number;
  departmentId?: number;
  functionalActivityId?: number;
}

const MEMBERS_EXPORT_QUERY_KEYS = [
  'search',
  'searchField',
  'sort',
  'order',
  'positionId',
  'departmentId',
  'functionalActivityId',
] as const;

export interface CreateMemberData {
  userId: number;
  positionIds: number[];
  functionalActivityIds?: number[];
}

export interface EligibleUser {
  id: number;
  name: string;
  registrationNumber: string | null;
  email: string;
}

class MembersApi {
  async getMembers(
    page = 1,
    limit = 100,
    search?: string,
    sort?: string,
    order?: string,
    filter?: MembersFilter,
    searchField?: MemberSearchField,
    permissionQuery?: MemberPermissionQuery
  ) {
    const query = {
      page,
      limit,
      search,
      sort,
      order,
      positionId: filter?.positionId,
      departmentId: filter?.departmentId,
      functionalActivityId: filter?.functionalActivityId,
      status: filter?.status,
      includeInactiveMembers: filter?.includeInactiveMembers ? 'true' : undefined,
      searchField,
      requiredPermissionAction: permissionQuery?.requiredPermissionAction,
      requiredPermissionSubject: permissionQuery?.requiredPermissionSubject,
      requiredPermissions: permissionQuery?.requiredPermissions,
      requiredPermissionsMatchMode: permissionQuery?.requiredPermissionsMatchMode,
      excludeCurrentUser: permissionQuery?.excludeCurrentUser ? 'true' : undefined,
    };

    const response: AxiosResponse<MemberResponse<BranchMember[]>> = await api.get(
      withQuery('/members', query)
    );

    return {
      data: response.data.data ?? [],
      metadata: response.data.metadata ?? {
        total: 0,
        page: 1,
        itemsPerPage: limit,
        totalPages: 0,
      },
    };
  }

  async getAllMembers(
    limit = 100,
    search?: string,
    permissionQuery?: MemberPermissionQuery,
    filter?: MembersFilter
  ) {
    const res = await this.getMembers(
      1,
      limit,
      search,
      undefined,
      undefined,
      filter,
      undefined,
      permissionQuery
    );
    return res.data;
  }

  async getAllMembersPaginated(
    limitPerPage = 100,
    search?: string,
    permissionQuery?: MemberPermissionQuery,
    filter?: MembersFilter
  ): Promise<BranchMember[]> {
    const first = await this.getMembers(
      1,
      limitPerPage,
      search,
      undefined,
      undefined,
      filter,
      undefined,
      permissionQuery
    );
    const combined = [...first.data];
    const totalPages = first.metadata.totalPages ?? 1;

    for (let page = 2; page <= totalPages; page++) {
      const res = await this.getMembers(
        page,
        limitPerPage,
        search,
        undefined,
        undefined,
        filter,
        undefined,
        permissionQuery
      );
      combined.push(...res.data);
    }

    return combined;
  }

  async createMember(data: CreateMemberData): Promise<void> {
    await api.post('/members', data);
  }

  async bulkDelete(ids: number[]): Promise<{ data: BulkDeleteResult }> {
    const response = await api.delete('/members/bulk', { data: { ids } });
    return { data: response.data.data };
  }

  async getEligibleUsers(
    search?: string,
    searchBy?: 'name' | 'registrationNumber'
  ): Promise<EligibleUser[]> {
    const params: Record<string, string> = {};
    if (search) params.search = search;
    if (searchBy) params.searchBy = searchBy;

    const response: AxiosResponse<MemberResponse<EligibleUser[]>> = await api.get(
      '/members/eligible-users',
      { params }
    );
    return response.data.data ?? [];
  }

  async getById(id: number): Promise<MemberDetailResponse> {
    const response = await api.get<ApiResponse<MemberDetailResponse>>(`/members/${id}`);
    if (!response.data.data) {
      throw new Error('Membro não encontrado');
    }
    return response.data.data;
  }

  async update(id: number, payload: UpdateMemberPayload): Promise<MemberDetailResponse> {
    const response = await api.patch<ApiResponse<MemberDetailResponse>>(`/members/${id}`, payload);
    if (!response.data.data) {
      throw new Error('Resposta inválida ao atualizar membro');
    }
    return response.data.data;
  }

  async delete(id: number): Promise<void> {
    await api.delete(`/members/${id}`);
  }

  async exportMembersExcel(
    params: ExportMembersExcelParams
  ): Promise<{ blob: Blob; exportedCount: number; filename: string }> {
    const queryParams = buildTruthySearchParams(params, MEMBERS_EXPORT_QUERY_KEYS);

    return requestExcelBlobExport(
      () =>
        api.get(`/members/export/excel?${queryParams.toString()}`, {
          responseType: 'blob',
        }),
      'membros.xlsx',
      'Tente novamente.'
    );
  }
}

export const membersApi = new MembersApi();
